import { apiClient } from '@/libs/api_client'
import axios from 'axios'
import { ApiResponse } from '@magnetar/admin-api-interfaces'
import { logger } from '@magnetar/utils'

interface UploadResponse {
  uuid: string
  meta?: Record<string, unknown>
}

interface SignedUrlResponse {
  uuid: string
  signedUrl: string
  storagePath: string
  expiresAt: string
}

interface FileMetadataResponse {
  uuid: string
  original_name: string
  mime_type: string
  storage_path: string
  size: number
  signed_url: string
  expires_at: string
  content_type: string
  hash: {
    md5_base64?: string
    md5_hex?: string
    sha256?: string
  }
}

export const uploadFileService = {
  /**
   * 取得檔案的直接下載 URL（透過 API 中繼）
   * @deprecated 建議使用 getFileDownloadUrl 以獲得更好的效能
   */
  getFileUrl: (uuid: string) => {
    return `/api/files/download/${uuid}`
  },

  /**
   * 取得 GCS Signed URL（直連下載，無大小限制）
   * 推薦用於大檔案下載
   */
  getFileDownloadUrl: async (uuid: string, token: string): Promise<string> => {
    try {
      const response = await apiClient.get<ApiResponse<FileMetadataResponse>>(
        `/api/files/download/${uuid}/metadata`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (response.data.success && response.data.data) {
        return response.data.data.signed_url
      }

      throw new Error(response.data.error?.message || '取得下載網址失敗')
    } catch (error) {
      logger.error('取得 Signed URL 失敗:', error)
      throw error
    }
  },

  /**
   * 取得檔案的完整 metadata 及 Signed URL
   */
  getFileMetadata: async (
    uuid: string,
    token: string,
  ): Promise<ApiResponse<FileMetadataResponse>> => {
    try {
      const response = await apiClient.get<ApiResponse<FileMetadataResponse>>(
        `/api/files/download/${uuid}/metadata`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )
      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<FileMetadataResponse>
      }
      return {
        success: false,
        error: {
          code: 'GET_FILE_METADATA_FAILED',
          message: '取得檔案資訊失敗',
        },
      }
    }
  },

  /**
   * 使用 Signed URL 方式上傳檔案（新方法，突破 32MB 限制）
   */
  uploadFile: async (
    file: File,
    folder: string,
    token: string,
  ): Promise<ApiResponse<UploadResponse>> => {
    try {
      // 步驟 1: 請求 Signed URL
      const signedUrlResponse = await apiClient.post<ApiResponse<SignedUrlResponse>>(
        '/api/upload/request-signed-url',
        {
          fileName: file.name,
          mimeType: file.type,
          folder,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      if (!signedUrlResponse.data.success || !signedUrlResponse.data.data) {
        return {
          success: false,
          error: {
            code: 'REQUEST_SIGNED_URL_FAILED',
            message: signedUrlResponse.data.error?.message || '請求上傳網址失敗',
          },
        }
      }

      const { uuid, signedUrl } = signedUrlResponse.data.data

      // 步驟 2: 直接上傳到 GCS
      await axios.put(signedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      })

      // 步驟 3: 通知後端上傳完成
      const completeResponse = await apiClient.post<ApiResponse<UploadResponse>>(
        '/api/upload/complete',
        {
          uuid,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      )

      return completeResponse.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<UploadResponse>
      }
      return {
        success: false,
        error: {
          code: 'UPLOAD_FILE_FAILED',
          message: error instanceof Error ? error.message : '上傳檔案失敗',
        },
      }
    }
  },

  /**
   * 舊的上傳方法（保留用於小檔案或向後相容）
   * @deprecated 建議使用新的 uploadFile 方法
   */
  uploadFileLegacy: async (
    file: File,
    folder: string,
    token: string,
  ): Promise<ApiResponse<UploadResponse>> => {
    try {
      // 建立 FormData
      const formData = new FormData()
      formData.append('file', file)
      formData.append('folder', folder)

      const response = await apiClient.post<ApiResponse<UploadResponse>>('/api/upload', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      })

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<UploadResponse>
      }
      return {
        success: false,
        error: {
          code: 'UPLOAD_FILE_FAILED',
          message: '上傳檔案失敗',
        },
      }
    }
  },

  deleteFile: async (uuid: string, token: string): Promise<ApiResponse<void>> => {
    try {
      const response = await apiClient.delete<ApiResponse<void>>(`/api/files/${uuid}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        return error.response.data as ApiResponse<void>
      }
      return {
        success: false,
        error: {
          code: 'DELETE_FILE_FAILED',
          message: '刪除檔案失敗',
        },
      }
    }
  },
}
